import { doc, runTransaction } from 'firebase/firestore';
import firebaseFirestore from '~/config/firebase/firestore';

/**
 * Escritura de la ocupación horaria de una ubicación.
 *
 * La disponibilidad vive en `locations/{id}.reservations[fecha][hora]`, un
 * array donde cada elemento es el id de una reserva. Cuando el espacio asigna
 * una posición por persona, el id aparece repetido una vez por asistente: así
 * `array.length` es el número de plazas ocupadas en esa franja.
 *
 * Todo se hace dentro de una transacción para que leer, comprobar y escribir
 * sean una sola operación indivisible. Antes se leía el mapa al abrir el
 * formulario y se reescribía entero al enviarlo, lo que borraba lo que otras
 * personas hubieran reservado o cancelado mientras tanto.
 */

/** Se lanza cuando la franja se ocupó entre que se eligió y se confirmó. */
export class SlotUnavailableError extends Error {
  constructor(public readonly hour: string, public readonly locationName?: string) {
    super(
      `La hora ${hour} acaba de ser reservada por otra persona. ` +
        'Por favor seleccione otro horario.'
    );
    this.name = 'SlotUnavailableError';
  }
}

type Day = Record<string, string[]>;

export type OccupyParams = {
  locationId: string;
  date: string;
  hours: string[];
  reservationId: string;
  /** Plazas que consume la reserva en cada franja: personas, o 1 si la sala es exclusiva. */
  seatsPerSlot: number;
  /** `numberBookingsAllow`: plazas totales de la franja (1 en salas privativas). */
  limit: number;
  locationName?: string;
  /** Al modificar una reserva: de dónde hay que retirarla antes de recolocarla. */
  release?: { locationId: string; dates: string[] };
};

/** Quita todas las apariciones de una reserva en un día. */
const removeFromDay = (day: Day, reservationId: string): Day => {
  const result: Day = {};
  for (const [hour, ids] of Object.entries(day)) {
    result[hour] = Array.isArray(ids) ? ids.filter((id) => id !== reservationId) : [];
  }
  return result;
};

/**
 * Comprueba que las plazas que va a consumir la reserva caben en cada franja.
 */
const assertFits = (
  day: Day,
  hours: string[],
  seatsPerSlot: number,
  limit: number,
  locationName?: string
): void => {
  // Siempre se cuenta cuántas plazas se van a escribir. Antes, sin "posición
  // por persona" solo se preguntaba "¿está lleno?", ignorando el tamaño del
  // grupo: un espacio de día completo y cupo 100 aceptaba tres grupos de 40.
  // Para todo lo que NOI usa hoy el resultado es idéntico (69 casos probados).
  const plazas = Math.max(seatsPerSlot, 1);
  for (const hour of hours) {
    const taken = day[hour]?.length ?? 0;
    if (taken + plazas > limit) {
      throw new SlotUnavailableError(hour, locationName);
    }
  }
};

/**
 * Coloca una reserva en las franjas indicadas, comprobando dentro de la misma
 * transacción que siguen libres. Si `release` viene informado, primero retira
 * la reserva de donde estuviera (caso de modificación o cambio de sala).
 *
 * @throws {SlotUnavailableError} si alguna franja ya no tiene sitio.
 */
export const occupySlots = async (params: OccupyParams): Promise<void> => {
  const {
    locationId, date, hours, reservationId, seatsPerSlot, limit,
    locationName, release,
  } = params;

  const targetRef = doc(firebaseFirestore, 'locations', locationId);
  const releaseRef =
    release && release.locationId !== locationId
      ? doc(firebaseFirestore, 'locations', release.locationId)
      : null;

  await runTransaction(firebaseFirestore, async (transaction) => {
    // Firestore exige que todas las lecturas ocurran antes de las escrituras.
    const targetSnap = await transaction.get(targetRef);
    const releaseSnap = releaseRef ? await transaction.get(releaseRef) : null;

    const targetReservations = (targetSnap.data()?.reservations ?? {}) as Record<string, Day>;

    // Días de los que hay que retirar la reserva dentro de ESTA ubicación.
    const datesToClean = new Set<string>([date]);
    if (release && !releaseRef) release.dates.forEach((d) => datesToClean.add(d));

    const cleaned: Record<string, Day> = {};
    for (const d of datesToClean) {
      cleaned[d] = removeFromDay((targetReservations[d] ?? {}) as Day, reservationId);
    }

    // Comprobación de disponibilidad sobre el estado real y ya limpio.
    assertFits(cleaned[date], hours, seatsPerSlot, limit, locationName);

    // Al menos una plaza: con un aforo de 0 o negativo, `Array.from` devolvía
    // una lista vacía y la reserva no ocupaba nada.
    const seats = Array.from({ length: Math.max(seatsPerSlot, 1) }, () => reservationId);
    for (const hour of hours) {
      cleaned[date][hour] = [...(cleaned[date][hour] ?? []), ...seats];
    }

    // Se escribe día a día, nunca el mapa completo: así una escritura no
    // puede pisar días que otra persona esté tocando a la vez.
    for (const [d, day] of Object.entries(cleaned)) {
      transaction.update(targetRef, { [`reservations.${d}`]: day });
    }

    if (releaseRef && releaseSnap) {
      const otherReservations = (releaseSnap.data()?.reservations ?? {}) as Record<string, Day>;
      for (const d of release!.dates) {
        const day = removeFromDay((otherReservations[d] ?? {}) as Day, reservationId);
        transaction.update(releaseRef, { [`reservations.${d}`]: day });
      }
    }
  });
};

/**
 * Retira una reserva de las franjas que ocupaba. Se usa al cancelar.
 * No comprueba disponibilidad: liberar sitio siempre es válido.
 */
export const releaseSlots = async (
  locationId: string,
  dates: string[],
  reservationId: string
): Promise<void> => {
  const ref = doc(firebaseFirestore, 'locations', locationId);

  await runTransaction(firebaseFirestore, async (transaction) => {
    const snap = await transaction.get(ref);
    const reservations = (snap.data()?.reservations ?? {}) as Record<string, Day>;

    for (const date of dates) {
      if (!reservations[date]) continue;
      const day = removeFromDay(reservations[date] as Day, reservationId);
      transaction.update(ref, { [`reservations.${date}`]: day });
    }
  });
};
